import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { MCTChartType } from 'components/DevicesDetail/MCT/ChartType/MCTChartType';
import { setCurrentMCTChartType } from 'pages/DevicesDetail/Slice/devicesDetailSlice';
import { useAppDispatch, useAppSelector } from 'slices';
import Toggle from 'components/Common/Toggle';
import HeatMapMonthDropdown from 'pages/DevicesDetail/MCT/MCTDetailCharts/Components/HeatMapMonthDropdown';
import HeatMapYearDropdown from 'pages/DevicesDetail/MCT/MCTDetailCharts/Components/HeatMapYearDropdown';

const CustomSelect = styled(Select)({
  '&.MuiInputBase-root': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    borderColor: 'red',
  },
  '& .MuiSelect-select': {
    padding: '10px 12px',
    paddingRight: '24px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #E0E0E0',
  },
});

interface Props {
  currentYear: string;
  selectedMonth: string | null;
  setCurrentYear: (year: string) => void;
  setSelectedMonth: (month: string | null) => void;
  setChartHeight: (height: number) => void;
  isBarChart: boolean;
  setIsBarChart: (val: boolean) => void;
  showSwitchChartType: boolean;
  isHeatmapCompare: boolean;
  setIsHeatmapCompare: (val: boolean) => void;
  setCompareYear: (year: string) => void;
  setCompareMonth: (month: string | null) => void;
}

const SMSTDetailChartsTitle: React.FC<Props> = ({
  currentYear,
  setCurrentYear,
  selectedMonth,
  setSelectedMonth,
  setChartHeight,
  isBarChart,
  setIsBarChart,
  showSwitchChartType = false,
  isHeatmapCompare,
  setIsHeatmapCompare,
  setCompareYear,
  setCompareMonth,
}) => {
  const { currentMCTChartType } = useAppSelector(state => state.DevicesDetailPages);
  const charts: string[] = Object.values(MCTChartType).slice(0, 5);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const handleChange = (event: SelectChangeEvent<unknown>) => {
    dispatch(setCurrentMCTChartType(event.target.value as MCTChartType));
  };

  return (
    <div className="card-header d-flex flex-column flex-md-row gap-2 align-items-center justify-content-between">
      <div className="d-flex align-items-center h4 mb-0 fw-bold gap-2 w-100">
        {currentMCTChartType === MCTChartType.HeatMapChart ? (
          <HeatMapYearDropdown
            currentYear={currentYear}
            setCurrentYear={setCurrentYear}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
          />
        ) : (
          <h4 className="d-flex mb-0 align-content-center justify-content-center">{currentMCTChartType} Chart</h4>
        )}
        {currentMCTChartType === MCTChartType.HeatMapChart && selectedMonth ? (
          <div className="d-flex gap-2 align-items-center justify-content-start">
            <HeatMapMonthDropdown selectedMonth={selectedMonth} setSelectedMonth={setSelectedMonth} />
            <ul className="pagination p-0 m-0">
              <button
                className="page-link"
                style={{ cursor: 'pointer', color: '#6A37D4', fontFamily: 'Poppins, sans-serif' }}
                onClick={() => {
                  setSelectedMonth(null);
                  setCompareMonth(null);
                  setChartHeight(400);
                }}
              >
                back to year
              </button>
            </ul>
          </div>
        ) : (
          ''
        )}
      </div>
      {currentMCTChartType === MCTChartType.HeatMapChart && (
        <div className="d-flex w-100 pe-md-4 py-4 py-md-0 mx-auto justify-content-md-end justify-content-center">
          <Toggle
            checked={!!isHeatmapCompare}
            onChange={() => {
              setCompareYear(currentYear);
              if (selectedMonth) {
                setCompareMonth(selectedMonth);
              }
              setIsHeatmapCompare(!isHeatmapCompare);
            }}
            label={t('ChartsAnalitics.all_traps_activity_chart.compare')}
            leftLabel={t('ChartsAnalitics.all_traps_activity_chart.default')}
            size="sm"
            className="m-0"
          />
        </div>
      )}
      <div className="d-flex">
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="demo-simple-select-standard-label" sx={{ fontFamily: 'Poppins, sans-serif' }}>
            Current Chart
          </InputLabel>
          <CustomSelect
            value={currentMCTChartType}
            onChange={handleChange}
            id="demo-simple-select-filled"
            label="Current Chart"
            sx={{
              width: 250,
              fontFamily: 'Poppins, sans-serif',
              '& .MuiSelect-select': {
                fontFamily: 'Poppins, sans-serif',
              },
            }}
          >
            {charts.map(chartName => (
              <MenuItem key={chartName} value={chartName} sx={{ fontFamily: 'Poppins, sans-serif' }}>
                {chartName}
              </MenuItem>
            ))}
          </CustomSelect>
        </FormControl>
      </div>
      {showSwitchChartType && setIsBarChart && (
        <div className="d-flex align-items-center">
          {' '}
          <Toggle
            checked={!!isBarChart}
            onChange={() => setIsBarChart(!isBarChart)}
            label={t('ChartsAnalitics.all_traps_activity_chart.bar_chart')}
            leftLabel={t('ChartsAnalitics.all_traps_activity_chart.line_chart')}
            size="sm"
            className="m-0"
          />
        </div>
      )}
    </div>
  );
};

export default SMSTDetailChartsTitle;
